/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  Button,
  Checkbox,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useState, useRef, useEffect } from 'react';
import { ROUTES } from 'routes/constants';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { CuffaAPI } from 'api/cuffa/cuffa.api';


// --- Interface de Dados ---
export interface ICuffaUpdateInstitutionalVisitsDto {
  received_institutional_visits: boolean;
  selected_institutions_ids?: string[];
  other_institutions_list?: string[];
}

const instituicoesPadrao = [
  { id: 'empaer', name: 'Empaer' },
  { id: 'embrapa', name: 'Embrapa' },
  { id: 'ifmt', name: 'IFMT' },
  { id: 'ongs', name: 'ONGs' },
  { id: 'senar', name: 'Senar' },
  { id: 'universidades_publicas', name: 'Universidades públicas' },
  { id: 'cooperativas', name: 'Cooperativas' },
  { id: 'empresas_privadas', name: 'Empresas privadas' },
  { id: 'incra', name: 'Incra' },
  { id: 'sebrae', name: 'Sebrae' },
  { id: 'universidades_privadas', name: 'Universidades privadas' },
];

export const normalizeInstitutionName = (name: string) => {
  const novaInstituicao = name.trim();
  if (!novaInstituicao) return '';
  return novaInstituicao.charAt(0).toUpperCase() + novaInstituicao.slice(1).toLowerCase();
};

export const isDuplicateInstitution = (list: string[], name: string) => {
  const low = name.toLowerCase();
  return list.map(i => i.toLowerCase()).includes(low);
};

export const buildVisitsPayload = (recebeuVisita: string, selected: string[], additional: string[]): ICuffaUpdateInstitutionalVisitsDto => {
  return {
    received_institutional_visits: recebeuVisita === 'sim',
    ...(recebeuVisita === 'sim' && {
      selected_institutions_ids: selected,
      other_institutions_list: additional,
    }),
  };
};

const validationSchema = Yup.object().shape({
  recebeuVisita: Yup.string().required('Este campo é obrigatório'),
});

export const VisitasInstituicoesIncluir: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  // --- ALTERAÇÃO 1: Adicionado estado de 'loading' ---
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instituicoesSelecionadas, setInstituicoesSelecionadas] = useState<string[]>([]);
  const [instituicoesAdicionais, setInstituicoesAdicionais] = useState<string[]>([]);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const advanceNext = useRef(false);
  // O useCuffaStore é mantido para a função salvarDadosParciais
  const { salvarDadosParciais } = useCuffaStore();

  // --- ALTERAÇÃO 2: useEffect modificado para buscar dados da API ---
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbarMessage("UUID da coleta não encontrado. Retorne e inicie novamente.");
        setOpenErrorSnackbar(true);
        setLoading(false);
        return;
      }
      // Primeiro tenta restaurar do localStorage 'cuffa-storage'
      try {
        const storageKey = 'cuffa-storage';
        const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.visitasInstituicoes;
            if (saved) {
              formik.setFieldValue('recebeuVisita', saved.recebeuVisita ?? (saved.received_institutional_visits ? 'sim' : 'nao'));
              setInstituicoesSelecionadas(saved.instituicoesSelecionadas || saved.selected_institutions_ids || []);
              setInstituicoesAdicionais(saved.instituicoesAdicionais || saved.other_institutions_list || []);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Falha ao parsear cuffa-storage.visitasInstituicoes', e);
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage', e);
      }

      // Fallback: buscar na API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        if (response && response.dados) {
          const apiData = response.dados;

          // Popula o formulário com os dados da API
          formik.setFieldValue('recebeuVisita', apiData.received_institutional_visits ? 'sim' : 'nao');
          setInstituicoesSelecionadas(apiData.selected_institutions_ids || []);
          setInstituicoesAdicionais(apiData.other_institutions_list || []);
        }
      } catch (error) {
        console.error('Erro ao buscar dados iniciais:', error);
        setSnackbarMessage('Não foi possível carregar os dados. Tente novamente.');
        setOpenErrorSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [uuid]); // A dependência 'formik' foi removida para evitar re-execução desnecessária

  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSuccessSnackbar(false);
    setOpenErrorSnackbar(false);
  };

  const formik = useFormik({
    initialValues: {
      recebeuVisita: '',
      novaInstituicao: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!uuid) {
        setSnackbarMessage("Não é possível salvar sem um UUID de coleta válido.");
        setOpenErrorSnackbar(true);
        return;
      }

      if (
        values.recebeuVisita === 'sim' &&
        instituicoesSelecionadas.length === 0 &&
        instituicoesAdicionais.length === 0
      ) {
        setSnackbarMessage('Se "Sim", você deve selecionar ou adicionar pelo menos uma instituição.');
        setOpenErrorSnackbar(true);
        return;
      }

      setSaving(true);

      const payload: ICuffaUpdateInstitutionalVisitsDto = {
        received_institutional_visits: values.recebeuVisita === 'sim',
        ...(values.recebeuVisita === 'sim' && {
          selected_institutions_ids: instituicoesSelecionadas,
          other_institutions_list: instituicoesAdicionais,
        }),
      };

      try {
        await CuffaAPI.updateInstitutionalVisit(uuid, payload);

        const proximaRota = ROUTES.CATEGORIA_FUNDIARIA_INCLUIR.replace(':uuid', uuid);
        salvarDadosParciais({
          visitasInstituicoes: {
            recebeuVisita: values.recebeuVisita,
            instituicoesSelecionadas,
            instituicoesAdicionais,
          },
          ultimaRota: proximaRota,
        });

        setSnackbarMessage('Dados salvos com sucesso!');
        setOpenSuccessSnackbar(true);

        if (advanceNext.current) {
          navigate(proximaRota);
        }
      } catch (error: any) {
        console.error('Erro ao salvar dados:', error);
        setSnackbarMessage(error.message || 'Erro ao salvar os dados. Tente novamente.');
        setOpenErrorSnackbar(true);
      } finally {
        setSaving(false);
        advanceNext.current = false;
      }
    }
  });

  const handleCheckboxChange = (id: string) => {
    setInstituicoesSelecionadas((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleAdicionarInstituicao = () => {
    const raw = formik.values.novaInstituicao;
    const novaInstituicao = normalizeInstitutionName(raw);
    if (!novaInstituicao) return;

    if (isDuplicateInstitution(instituicoesAdicionais, novaInstituicao)) {
      setSnackbarMessage('Esta instituição já foi adicionada.');
      setOpenErrorSnackbar(true);
      return;
    }
    setInstituicoesAdicionais([...instituicoesAdicionais, novaInstituicao]);
    formik.setFieldValue('novaInstituicao', '');
  };

  const handleRemoverInstituicao = (index: number) => {
    setInstituicoesAdicionais(instituicoesAdicionais.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    advanceNext.current = false;
    await formik.submitForm();
  };

  const handleAdvance = async () => {
    advanceNext.current = true;
    await formik.submitForm();
  };


  // --- ALTERAÇÃO 3: Renderização condicional para o estado de 'loading' ---
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Visitas de Instituições" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visitas de Instituições" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Visitas de Instituições" width="100%">
            <form onSubmit={formik.handleSubmit}>
              <Box marginBottom="2rem">
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  A unidade familiar recebeu a visita de alguma instituição prestadora de assistência, exceto a prefeitura, no último ano? *
                </Typography>
                <RadioGroup
                  row
                  name="recebeuVisita"
                  value={formik.values.recebeuVisita}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="nao" control={<Radio />} label="Não" />
                </RadioGroup>
                {formik.touched.recebeuVisita && formik.errors.recebeuVisita && (
                  <Typography color="error" variant="caption">
                    {formik.errors.recebeuVisita}
                  </Typography>
                )}
              </Box>

              {formik.values.recebeuVisita === 'sim' && (
                <Box
                  sx={{
                    backgroundColor: '#f5faff',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    marginBottom: '2rem',
                  }}
                >
                  <Typography fontWeight="bold" mb={2}>
                    Marque todas as instituições que visitaram a unidade familiar:
                  </Typography>
                  <Grid container spacing={2}>
                    {instituicoesPadrao.map((instituicao) => (
                      <Grid item xs={12} sm={6} md={4} key={instituicao.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={instituicoesSelecionadas.includes(instituicao.id)}
                              onChange={() => handleCheckboxChange(instituicao.id)}
                            />
                          }
                          label={instituicao.name.toUpperCase()}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Box mt={4}>
                    <Typography fontWeight="bold" gutterBottom>
                      Outras instituições:
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <TextField
                        name="novaInstituicao"
                        value={formik.values.novaInstituicao.toLocaleUpperCase()}
                        onChange={formik.handleChange}
                        inputProps={{ maxLength: 100 }}
                        size="small"
                        fullWidth
                        placeholder='Digite o nome e clique em Adicionar'
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleAdicionarInstituicao}
                        disabled={!formik.values.novaInstituicao.trim()}
                        sx={{ height: '40px', minWidth: '120px', color: 'black', borderColor: 'black', textTransform: 'none' }}
                      >
                        + Adicionar
                      </Button>
                    </Box>

                    <Box mt={2}>
                      {instituicoesAdicionais.map((nome, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={1}
                        >
                          <Typography>{nome.toUpperCase()}</Typography>
                          <IconButton onClick={() => handleRemoverInstituicao(index)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
                <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.IDENTIFICACAO_FAMILIAR_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>
                  Retornar
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleSave} disabled={saving}>
                  {saving && !advanceNext.current ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
                </Button>
                <Button variant="contained" color="primary" onClick={handleAdvance} disabled={saving}>
                  {saving && advanceNext.current && <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />}
                  Avançar
                </Button>
              </Box>
            </form>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar open={openSuccessSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};

export default VisitasInstituicoesIncluir;
